import { AnimatePresence } from 'framer-motion';
import { FC, useEffect, useState } from 'react';
import { DateRange } from 'react-day-picker';
import { twMerge } from 'tailwind-merge';

import { CalendarIconVariant } from '../../../../assets/icons/calendar-icon';
import {
  CalendarIcon,
  ChevronDownIcon,
  CrossIcon,
  RefreshIcon,
} from '../../../../assets/icons/index';
import { RefreshIconVariant } from '../../../../assets/icons/refresh-icon';
import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import Button from '../../../../components/button/index';
import { Calendar } from '../../../../components/calendar/index';
import { Checkbox } from '../../../../components/checkbox/index';
import { CrossfadePanel, StaggerFadeIn, StaggerItem } from '../../../../components/motion/index';
import { PaginationController } from '../../../../components/pagination-controller/index';
import { Popover, PopoverContent, PopoverTrigger } from '../../../../components/popover/index';
import { EventHooks, useFormat, useModalState, usePagination } from '../../../../hooks/index';
import {
  EventTypeBackend,
  EventUnion,
  isDividendTimelineEvent,
  isPendingLifecycleStatus,
  SortByDate,
  sortByDateMap,
  sortByDateTitle,
  SortByType,
  sortByTypeTitle,
} from '../../../../types/events.types';
import { EventLifecycleWrapper } from '../../components/event-lifecycle-wrapper';
import {
  EventHistoryRowPlaceholder,
  EventsHistorySectionPlaceholder,
  EventsTransactionViewPlaceholder,
} from '../../components/events-loading-placeholders';
import { EmptyEventHistory } from '../../empty-event-history';
import { EditEventStepVariant } from '../../event-form/event-form.types';
import { EventItem } from './event-item';
import { Dividend } from './event-table-types/dividend';
import { DividendEventItem } from './event-table-types/dividend-event-item';
import { eventSelectedTabStore } from './history.store';

export const itemsToFetchMap = [5, 10, 20, 50, 100];

type HistoryProps = {
  companyId: string;
  onCloseRound: (id: string) => void;
  onDelete: (id: string, eventType: EventUnion['type']) => void;
  onEdit: (id: string, step?: EditEventStepVariant) => void;
  onTransactionDelete: (event: EventUnion, transactionId: string) => void;
  onTransactionEdit: (
    id: string,
    transactionId: string,
    previewStep?: EditEventStepVariant,
  ) => void;
  onTransactionView: (
    id: string,
    transactionId: string,
    previewStep?: EditEventStepVariant,
  ) => void;
  onView: (id: string, step?: EditEventStepVariant) => void;
};

const sortByTypeValues = Object.keys(sortByTypeTitle);

import { COMPANY_INCORPORATION_EDIT_PREVIEW_STEP } from '../../event-form/step-components/company-incorporation-steps/company-incorporation-edit-preview';
import { grantEditPreviewStepFromPersistedGrant } from '../../utils/grant-edit-signature-flow.util';

const grantHistoryEditPreviewStep = (event: EventUnion): EditEventStepVariant => {
  if (event.type !== EventTypeBackend.GRANT) return 4;
  return grantEditPreviewStepFromPersistedGrant(event);
};

const historyEditPreviewStep = (event: EventUnion): EditEventStepVariant => {
  if (event.type === EventTypeBackend.COMPANY_INCORPORATION) {
    return COMPANY_INCORPORATION_EDIT_PREVIEW_STEP;
  }

  return grantHistoryEditPreviewStep(event);
};

export const History: FC<HistoryProps> = ({
  companyId,
  onEdit,
  onView,
  onDelete,
  onTransactionDelete,
  onTransactionView,
  onTransactionEdit,
  onCloseRound,
}) => {
  const [currentPage, setCurrentPage, resetPagination] = usePagination('events-history');
  const [itemsToFetch, setItemsToFetch] = useState(itemsToFetchMap[1]);

  const selectedTypeView = eventSelectedTabStore(({ selectedViewBy }) => selectedViewBy);
  const setTypeView = eventSelectedTabStore(({ setSelectedViewBy }) => setSelectedViewBy);
  const isDividendTab = selectedTypeView === SortByType.DIVIDEND;

  const { format } = useFormat();
  const [selectedDateFilter, setDateFilter] = useState(SortByDate.ALL_TIME);
  const [selectedCustomDate, setCustomDate] = useState<DateRange>({
    from: undefined,
    to: undefined,
  });
  const [selectedCustomDateFilter, setCustomDateFilter] = useState<DateRange>({
    from: undefined,
    to: undefined,
  });

  const handleSelect = () => {
    setCustomDateFilter(selectedCustomDate);
    handleCloseModal();
  };

  const transactionView = selectedTypeView !== null;

  const {
    entries: entriesData,
    isSearchResultEmpty,
    totalPages,
    refetch,
    isLoading,
  } = EventHooks.useEvents({
    companyId,
    dateFilter: selectedDateFilter,
    typesFilter:
      isDividendTab || !selectedTypeView
        ? undefined
        : selectedTypeView === SortByType.EXERCISE
          ? [selectedTypeView, SortByType.APPROVAL]
          : [selectedTypeView],
    customDateFilter: selectedCustomDateFilter,
    take: isDividendTab ? 0 : Number(itemsToFetch),
    currentPage,
    transactionView,
    // "All" tab only — the type tabs stay events-only and Dividends has its own tab.
    includeDividends: !selectedTypeView && !transactionView,
    // Timeline renders a pending create as a skeleton row, so it wants the client placeholder.
    includePendingCreates: true,
  });

  const { isOpen, toggler, handleCloseModal } = useModalState();

  const handleReset = () => {
    setCustomDateFilter({ from: undefined, to: undefined });
    setCustomDate({ from: undefined, to: undefined });
  };

  const handleCancel = () => {
    setDateFilter(SortByDate.LAST_30_DAYS);
  };

  const isSelectedSomeFilter = selectedCustomDate.from !== undefined || Boolean(selectedTypeView);

  const entries = transactionView ? (entriesData[0] ? [entriesData[0]] : []) : entriesData;
  const isEmpty = !entries.length;

  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) {
      setCurrentPage(1);
    }
  }, [itemsToFetch, totalPages, currentPage, setCurrentPage]);

  return (
    <div className="flex w-full flex-col items-center gap-6 rounded-md p-4 shadow-sm">
      {(!isEmpty || isSelectedSomeFilter || isLoading) && (
        <div className="flex w-full flex-col gap-4">
          <div className="flex w-full items-center justify-between">
            <div className="flex items-center gap-2">
              <RefreshIcon variant={RefreshIconVariant.EVENTS} />
              <span className="text-nowrap text-base font-semibold text-gray-700">
                <AppFormattedMessage id="EVENT_HISTORY" />
              </span>
            </div>
            <div className="flex h-9 w-fit items-center gap-2">
              <Popover onOpenChange={toggler} open={isOpen}>
                <PopoverTrigger className="h-full w-fit rounded border bg-white data-[state=open]:bg-gray-50">
                  <div className="flex items-center justify-between gap-2 rounded border-gray-200 px-3">
                    <CalendarIcon variant={CalendarIconVariant.WITH_NUMBER} />
                    <span className="text-sm font-[450] text-gray-700">
                      {sortByDateTitle[selectedDateFilter]}
                    </span>
                    <ChevronDownIcon className="h-4 w-4 text-gray-400" />
                  </div>
                </PopoverTrigger>
                {selectedDateFilter === SortByDate.CUSTOM_DATE ? (
                  <PopoverContent className="w-fit flex-col gap-4 overflow-hidden rounded-lg bg-gray-100 p-0">
                    <div className="flex w-full flex-col rounded-b-lg bg-white shadow-sm">
                      <div className="flex w-full items-center justify-between px-4 py-3">
                        <span className="text-xl font-semibold text-gray-700">
                          <AppFormattedMessage id="CUSTOM_DATE" />
                        </span>
                        <Button
                          className="h-fit w-fit rounded p-1 transition-colors hover:bg-gray-200"
                          onClick={handleCancel}
                          styleType="NONE"
                        >
                          <CrossIcon className="size-5" />
                        </Button>
                      </div>
                      <div className="flex w-full gap-6 p-4">
                        <div className="flex w-full flex-col gap-2">
                          <span className="text-sm font-[450] text-gray-700">
                            <AppFormattedMessage id="START_DATE" />
                          </span>
                          <span className="h-10 rounded-t border-b border-gray-700 bg-gray-50 p-3 text-sm font-[450] text-gray-700">
                            {selectedCustomDate.from
                              ? format(selectedCustomDate.from, 'dd/MM/yyyy')
                              : ''}
                          </span>
                        </div>
                        <div className="flex w-full flex-col gap-2">
                          <span className="text-sm font-[450] text-gray-700">
                            <AppFormattedMessage id="END_DATE" />
                          </span>
                          <span className="h-10 rounded-t border-b border-gray-700 bg-gray-50 p-3 text-sm font-[450] text-gray-700">
                            {selectedCustomDate.to
                              ? format(selectedCustomDate.to, 'dd/MM/yyyy')
                              : ''}
                          </span>
                        </div>
                      </div>
                      <div className="centered flex w-full">
                        <Calendar
                          className="px-2"
                          initialFocus
                          mode="range"
                          onSelect={(date) => date && setCustomDate(date)}
                          selected={selectedCustomDate}
                        />
                      </div>
                    </div>
                    <div className="-mt-4 flex w-full justify-between gap-20 px-6 py-4">
                      <Button
                        className="text-sm font-[450] text-gray-700"
                        onClick={handleCancel}
                        styleType="NONE"
                      >
                        <AppFormattedMessage id="CANCEL" />
                      </Button>
                      <div className="flex gap-3">
                        <Button
                          className="h-9 w-fit rounded border border-gray-300 bg-white px-4 text-sm font-[450]"
                          onClick={handleReset}
                          styleType="NONE"
                        >
                          <AppFormattedMessage id="RESET" />
                        </Button>
                        <Button
                          className="h-9 w-fit rounded border px-4 text-sm font-[550] text-white"
                          disabled={!selectedCustomDate.from || !selectedCustomDate.to}
                          onClick={handleSelect}
                        >
                          <AppFormattedMessage id="SELECT" />
                        </Button>
                      </div>
                    </div>
                  </PopoverContent>
                ) : (
                  <PopoverContent className="flex w-fit flex-col gap-[2px] rounded p-2 font-normal shadow-2xl [&>*]:font-inter [&>*]:font-normal">
                    {sortByDateMap.map(([sortKey, sortTitle]) => (
                      <div
                        className="flex w-full items-center gap-2 rounded p-[6px] pr-8 text-sm text-gray-700"
                        key={sortKey}
                      >
                        <Checkbox
                          checked={sortKey === selectedDateFilter}
                          className="rounded-full"
                          onChange={() => {
                            setDateFilter(sortKey);
                          }}
                        />
                        <span className="text-sm font-normal text-gray-700">{sortTitle}</span>
                      </div>
                    ))}
                  </PopoverContent>
                )}
              </Popover>
            </div>
          </div>
          <div className="flex w-full justify-between gap-6 overflow-hidden overflow-x-auto border-b border-gray-200">
            <Button
              className={twMerge(
                'w-fit text-nowrap rounded-none border-b-2 border-b-transparent px-1 pb-4 text-sm font-[450] text-gray-400',
                selectedTypeView === null && 'border-brand-700 font-[550] text-brand-700',
              )}
              onClick={() => setTypeView(null)}
              styleType="NONE"
            >
              <AppFormattedMessage id="ALL" />
            </Button>
            {sortByTypeValues.map(
              (type) =>
                sortByTypeTitle[type] &&
                type !== SortByType.APPROVAL && (
                  <Button
                    className={twMerge(
                      'w-fit text-nowrap rounded-none border-b-2 border-b-transparent pb-4 text-sm font-[450] text-gray-400',
                      type === selectedTypeView && 'border-brand-700 font-[550] text-brand-700',
                    )}
                    key={type}
                    onClick={() => {
                      resetPagination();
                      setTypeView(type);
                    }}
                    styleType="NONE"
                  >
                    <AppFormattedMessage id={sortByTypeTitle[type]} />
                  </Button>
                ),
            )}
          </div>
        </div>
      )}
      <div className={twMerge('flex h-full w-full flex-col items-center gap-4', isEmpty && 'flex')}>
        <AnimatePresence mode="wait">
          <CrossfadePanel
            className="flex w-full flex-col items-center gap-4"
            key={`history-${selectedTypeView ?? 'all'}`}
            panelKey={`history-${selectedTypeView ?? 'all'}`}
            translate={false}
          >
            {isDividendTab ? (
              <Dividend companyId={companyId} />
            ) : isLoading ? (
              transactionView ? (
                <EventsTransactionViewPlaceholder />
              ) : (
                <EventsHistorySectionPlaceholder />
              )
            ) : !isEmpty ? (
              <StaggerFadeIn
                className="flex w-full flex-col gap-4"
                key={`${currentPage}-${itemsToFetch}`}
              >
                {entries.map((event) =>
                  isDividendTimelineEvent(event) ? (
                    <StaggerItem key={event.eventId}>
                      <DividendEventItem companyId={companyId} payout={event} />
                    </StaggerItem>
                  ) : isPendingLifecycleStatus(event.lifecycleStatus) ? (
                    <StaggerItem key={event.eventId}>
                      <EventLifecycleWrapper event={event}>
                        {transactionView ? (
                          <EventsTransactionViewPlaceholder rows={1} />
                        ) : (
                          <EventHistoryRowPlaceholder />
                        )}
                      </EventLifecycleWrapper>
                    </StaggerItem>
                  ) : (
                    <StaggerItem key={event.eventId}>
                      <EventLifecycleWrapper event={event}>
                        <EventItem
                          companyId={companyId}
                          event={event}
                          onAddInvestment={() => onEdit(event.eventId, 2)}
                          onCloseRound={() => onCloseRound(event.eventId)}
                          onDelete={() => onDelete(event.eventId, event.type)}
                          onEdit={() => onEdit(event.eventId, historyEditPreviewStep(event))}
                          onTransactionDelete={onTransactionDelete}
                          onTransactionEdit={(id, transactionId) =>
                            onTransactionEdit(id, transactionId, historyEditPreviewStep(event))
                          }
                          onTransactionView={(id, transactionId) =>
                            onTransactionView(id, transactionId, historyEditPreviewStep(event))
                          }
                          onView={() => onView(event.eventId, historyEditPreviewStep(event))}
                          refetch={refetch}
                          selectedCustomDateFilter={selectedCustomDateFilter}
                          selectedDateFilter={selectedDateFilter}
                          transactionView={transactionView}
                        />
                      </EventLifecycleWrapper>
                    </StaggerItem>
                  ),
                )}
              </StaggerFadeIn>
            ) : (
              <EmptyEventHistory
                isDisplayedAddEvent={!isSearchResultEmpty}
                text={
                  isSearchResultEmpty
                    ? 'No results match this search term Please try again'
                    : undefined
                }
                title={
                  isSearchResultEmpty ? <AppFormattedMessage id="NO_EVENTS_FOUND" /> : undefined
                }
                type={selectedTypeView || undefined}
              />
            )}
          </CrossfadePanel>
        </AnimatePresence>
        {!transactionView && !isDividendTab && (
          <PaginationController
            className="shrink-0"
            currentPage={currentPage}
            itemsToFetch={itemsToFetch}
            itemsToFetchMap={itemsToFetchMap}
            onClick={setCurrentPage}
            onFetchAmountChange={setItemsToFetch}
            totalPages={totalPages}
          />
        )}
      </div>
    </div>
  );
};
